import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { formatUTCDateToEastern } from '../utils/dateUtils';
import { 
  UsersIcon, 
  ServerIcon, 
  CurrencyDollarIcon,
  ChartBarIcon,
  EyeIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ShoppingCartIcon
} from '@heroicons/react/24/outline';

const AdminDashboardPage = () => {
  const { user } = useAuth();
  const [groupedPlans, setGroupedPlans] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [plansResponse, statsResponse] = await Promise.all([
        api.get('/api/admin/plans-by-user'),
        api.get('/api/admin/dashboard-stats')
      ]);
      
      setGroupedPlans(plansResponse.data.groupedPlans);
      setStats(statsResponse.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'cancelled':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      case 'suspended':
        return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
      case 'pending':
        return <ClockIcon className="h-4 w-4 text-blue-500" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateTotalRevenue = (plans) => {
    return plans.reduce((total, plan) => total + plan.price, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - The Host Bros</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-2">
                Welcome back, {user?.firstName}! Manage your hosting business.
              </p>
            </div>
            <Link to="/admin/carts" className="btn-primary flex items-center self-start">
              <ShoppingCartIcon className="h-5 w-5 mr-2" />
              Manage Carts
            </Link>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="bg-primary-100 p-3 rounded-lg">
                  <UsersIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Customers</p>
                  <p className="text-2xl font-bold text-gray-900">{groupedPlans.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="bg-green-100 p-3 rounded-lg">
                  <ServerIcon className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Plans</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.overview?.totalPlans || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <CheckCircleIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Plans</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.overview?.activePlans || 0}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="bg-yellow-100 p-3 rounded-lg">
                  <CurrencyDollarIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Monthly Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${stats.monthlyRevenue?.[0]?.revenue || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Plan Type Distribution */}
          {stats.planTypeStats && stats.planTypeStats.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Plan Distribution</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {stats.planTypeStats.map((stat) => (
                  <div key={stat._id} className="text-center">
                    <div className="text-2xl font-bold text-gray-900 capitalize">{stat._id}</div>
                    <div className="text-sm text-gray-600">{stat.count} plans</div>
                    <div className="text-lg font-semibold text-primary-600">
                      ${stat.totalRevenue} revenue
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Customers and Plans */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">All Customers & Plans</h2>
            </div>

            <div className="p-6">
              {groupedPlans.length === 0 ? (
                <div className="text-center py-12">
                  <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No customers yet</h3>
                  <p className="text-gray-600">
                    Customers will appear here once they sign up and purchase hosting plans.
                  </p>
                </div>
              ) : (
                <div className="space-y-8">
                  {groupedPlans.map((group) => (
                    <div
                      key={group.user._id}
                      className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                    >
                      {/* Customer Info */}
                      <div className="flex justify-between items-start mb-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {group.user.firstName} {group.user.lastName}
                          </h3>
                          <p className="text-gray-600">{group.user.email}</p>
                          {group.user.phone && (
                            <p className="text-gray-600">{group.user.phone}</p>
                          )}
                          <p className="text-sm text-gray-500">
                            Member since {formatUTCDateToEastern(group.user.createdAt)}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => {
                              setSelectedUser(group);
                              setShowUserModal(true);
                            }}
                            className="btn-secondary flex items-center"
                          >
                            <EyeIcon className="h-4 w-4 mr-2" />
                            View Details
                          </button>
                        </div>
                      </div>

                      {/* Plans */}
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900">
                          Hosting Plans ({group.plans.length})
                        </h4>
                        {group.plans.map((plan) => (
                          <div
                            key={plan._id}
                            className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="flex items-center mb-2">
                                  <h5 className="font-medium text-gray-900 capitalize">
                                    {plan.planType} Hosting
                                  </h5>
                                  <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(plan.status)}`}>
                                    {plan.status}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">
                                  {plan.domainName ? `Domain: ${plan.domainName}` : 'No domain configured'}
                                </p>
                                <p className="text-sm text-gray-600">
                                  Billing: ${plan.price}/{plan.billingCycle}
                                </p>
                                {plan.squareSubscriptionId && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    <strong>Square Subscription ID:</strong> {plan.squareSubscriptionId}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-gray-600">
                                  Next billing: {formatUTCDateToEastern(plan.nextBillingDate)}
                                </p>
                                {plan.notes && (
                                  <p className="text-sm text-gray-500 mt-1">
                                    Note: {plan.notes}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Total Revenue */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-900">Total Revenue:</span>
                          <span className="text-lg font-bold text-primary-600">
                            ${calculateTotalRevenue(group.plans)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Customer Details
                </h3>
                <button
                  onClick={() => {
                    setShowUserModal(false);
                    setSelectedUser(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Customer Information</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p><strong>Name:</strong> {selectedUser.user.firstName} {selectedUser.user.lastName}</p>
                    <p><strong>Email:</strong> {selectedUser.user.email}</p>
                    {selectedUser.user.phone && (
                      <p><strong>Phone:</strong> {selectedUser.user.phone}</p>
                    )}
                    <p><strong>Member since:</strong> {formatUTCDateToEastern(selectedUser.user.createdAt)}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Hosting Plans</h4>
                  <div className="space-y-3">
                    {selectedUser.plans.map((plan) => (
                      <div key={plan._id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h5 className="font-medium text-gray-900 capitalize">
                              {plan.planType} Hosting
                            </h5>
                            <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(plan.status)}`}>
                              {plan.status}
                            </span>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">${plan.price}</p>
                            <p className="text-sm text-gray-600 capitalize">per {plan.billingCycle}</p>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                          <div>
                            <p><strong>Domain:</strong> {plan.domainName || 'Not configured'}</p>
                            <p><strong>Square Subscription ID:</strong> {plan.squareSubscriptionId || 'N/A'}</p>
                          </div>
                          <div>
                            <p><strong>Next Billing:</strong> {formatUTCDateToEastern(plan.nextBillingDate)}</p>
                            <p><strong>Created:</strong> {formatUTCDateToEastern(plan.createdAt)}</p>
                          </div>
                        </div>

                        {plan.notes && (
                          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                            <p className="text-sm text-yellow-800">
                              <strong>Admin Notes:</strong> {plan.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-900">Total Revenue:</span>
                    <span className="text-xl font-bold text-primary-600">
                      ${calculateTotalRevenue(selectedUser.plans)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDashboardPage; 